import { Injectable, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '@env/environment';
import type {
  LoginPayload,
  LoginResponse,
  PasswordResetCompletePayload,
  PasswordResetLinkVerifyPayload,
  PasswordResetRequestPayload,
  PasswordResetVerifyPayload,
  PasswordResetVerifyResponse,
  User,
  ValidateResponse,
} from '@app/interfaces/auth/auth.interfaces';

export type {
  LoginPayload,
  LoginResponse,
  PasswordResetCompletePayload,
  PasswordResetLinkVerifyPayload,
  PasswordResetRequestPayload,
  PasswordResetVerifyPayload,
  PasswordResetVerifyResponse,
  User,
  ValidateResponse,
} from '@app/interfaces/auth/auth.interfaces';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly http = inject(HttpClient);
  private readonly authUrl = `${environment.apiUrl}/auth`;

  readonly user = signal<User | null>(this.getStoredUser());

  get isLoggedIn(): boolean {
    return this.hasUser();
  }

  get isAdmin(): boolean {
    const role = this.user()?.role;
    return role === 'super_admin' || role === 'admin';
  }

  get redirectPath(): string {
    return this.isAdmin ? '/admin' : '/';
  }

  getAuthToken(): string {
    return this.getStoredValue('token') ?? '';
  }

  login(payload: LoginPayload): Observable<LoginResponse> {
    return this.http.post<LoginResponse>(`${this.authUrl}/login`, payload);
  }

  logout(): Observable<void> {
    return this.http.post<void>(`${this.authUrl}/logout`, {});
  }

  validate(): Observable<ValidateResponse> {
    return this.http.get<ValidateResponse>(`${this.authUrl}/validate`);
  }

  requestPasswordReset(payload: PasswordResetRequestPayload): Observable<{ message: string }> {
    return this.http.post<{ message: string }>(`${this.authUrl}/forgot-password`, payload);
  }

  verifyPasswordReset(
    payload: PasswordResetVerifyPayload,
  ): Observable<PasswordResetVerifyResponse> {
    return this.http.post<PasswordResetVerifyResponse>(
      `${this.authUrl}/forgot-password/verify`,
      payload,
    );
  }

  verifyPasswordResetLink(
    payload: PasswordResetLinkVerifyPayload,
  ): Observable<{ message: string; email: string }> {
    return this.http.post<{ message: string; email: string }>(
      `${this.authUrl}/forgot-password/link/verify`,
      payload,
    );
  }

  completePasswordReset(payload: PasswordResetCompletePayload): Observable<{ message: string }> {
    return this.http.post<{ message: string }>(`${this.authUrl}/forgot-password/reset`, payload);
  }

  updateProfile(payload: {
    name?: string;
    email?: string;
    phone?: string | null;
    organization?: string | null;
    position?: string | null;
  }): Observable<{ message: string; user: User }> {
    return this.http.post<{ message: string; user: User }>(
      `${environment.apiUrl}/protected/profile`,
      payload,
    );
  }

  saveLogin(response: LoginResponse): User | null {
    const user = response.user;
    if (!user) {
      return null;
    }

    this.setStoredValue('token', response.token);
    this.setStoredValue('user', JSON.stringify(user));
    this.user.set(user);

    return user;
  }

  saveValidatedSession(response: ValidateResponse): boolean {
    if (!response.user) {
      return false;
    }

    this.saveUpdatedUser(response.user);
    return true;
  }

  saveUpdatedUser(user: User): void {
    this.setStoredValue('user', JSON.stringify(user));
    this.user.set(user);
  }

  clearSession(): void {
    this.removeStoredValue('token');
    this.removeStoredValue('user');
    this.user.set(null);
  }

  hasUser(): boolean {
    return this.user() !== null;
  }

  hasRequiredRole(allowedRoles: readonly string[]): boolean {
    const role = this.user()?.role;
    return role ? allowedRoles.includes(role) : false;
  }

  private getStoredUser(): User | null {
    const storedUser = this.getStoredValue('user');
    if (!storedUser) {
      return null;
    }

    try {
      return JSON.parse(storedUser) as User;
    } catch {
      this.removeStoredValue('user');
      return null;
    }
  }

  private getStoredValue(key: string): string | null {
    return this.canUseStorage() ? localStorage.getItem(key) : null;
  }

  private setStoredValue(key: string, value: string): void {
    if (this.canUseStorage()) {
      localStorage.setItem(key, value);
    }
  }

  private removeStoredValue(key: string): void {
    if (this.canUseStorage()) {
      localStorage.removeItem(key);
    }
  }

  private canUseStorage(): boolean {
    return typeof window !== 'undefined' && typeof localStorage !== 'undefined';
  }
}
